/*
Purpose: Calendar (v2) Print — same A3 PDF export as the legacy Calendar generatePDF.
*/

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Moment from 'moment';

import { getMomentLocaleTimeFormat } from '../Calendar-util';
import { CALENDAR_VIEW, HOURS_FILTER } from './constants';
import { capitalize } from './session-model';

/**
 * Download the visible day/week sessions as an A3 PDF, matching production Calendar print.
 *
 * @param {Object} params
 * @param {Object[]} params.records Raw API session rows (`getAllSessionFromTimeCal_defer`).
 * @param {string} params.view `CALENDAR_VIEW.DAY` or `WEEK`.
 * @param {string} params.hoursFilter `HOURS_FILTER` value (checkOP).
 * @param {string} params.startDate Inclusive `YYYY-MM-DD` for the printed range.
 * @param {string} params.endDate Inclusive `YYYY-MM-DD` for the printed range.
 * @param {string} params.locale Centre locale for time labels.
 */
export function generateCalendarPdf({
  records,
  view,
  hoursFilter,
  startDate,
  endDate,
  locale,
}) {
  const doc = new jsPDF({ format: 'a3', orientation: 'portrait' });
  const center_name = sessionStorage.getItem('center_name') || 'calendar';

  const calView = view === CALENDAR_VIEW.DAY ? ' (Day)' : view === CALENDAR_VIEW.WEEK ? ' (Week)' : '';

  doc.text(center_name + calView, 10, 10);

  const opView =
    String(hoursFilter) === String(HOURS_FILTER.ALL_HOURS)
      ? 'All Hours View'
      : 'Operating Hours View';
  doc.text(opView, doc.internal.pageSize.getWidth() - 10 - doc.getTextWidth(opView), 10);

  const caldate =
    view === CALENDAR_VIEW.DAY
      ? Moment(startDate, 'YYYY-MM-DD').format('DD MMM YYYY')
      : view === CALENDAR_VIEW.WEEK
        ? `${Moment(startDate, 'YYYY-MM-DD').format('DD MMM YYYY')} to ${Moment(
            endDate,
            'YYYY-MM-DD'
          ).format('DD MMM YYYY')}`
        : '';

  const centerX = doc.internal.pageSize.getWidth() / 2 - doc.getTextWidth(caldate) / 2;
  doc.text(caldate, centerX, 20);

  const tableColumn = ['Date', 'Start - End Time', 'Name', 'Tutor', 'Students'];

  const startBound = new Date(startDate);
  const endBound = new Date(endDate);
  const filteredRecords = (Array.isArray(records) ? records : []).filter((record) => {
    const sesDate = new Date(record.ses_date);
    return sesDate >= startBound && sesDate <= endBound;
  });

  const sortData = filteredRecords.sort((a, b) => {
    const aKey = a.start || `${a.ses_date}T${a.sest_start_time || '00:00:00'}`;
    const bKey = b.start || `${b.ses_date}T${b.sest_start_time || '00:00:00'}`;
    return new Date(aKey) - new Date(bKey);
  });

  const tableRows = [];

  sortData.forEach((event) => {
    const startTime = event.sest_start_time
      ? getMomentLocaleTimeFormat(event.sest_start_time, 'HH:mm:ss', locale)
      : '';
    const endTime = event.ses_end_time
      ? getMomentLocaleTimeFormat(event.ses_end_time, 'HH:mm:ss', locale)
      : '';
    const timeRange = startTime && endTime ? `${startTime} - ${endTime}` : startTime || endTime;

    const ses_name = event.isschedule == 1 ? event.title : event.sest_name || '';
    const tutor_name = event.isschedule == 1 ? event.title : event.title || '';
    const cls_typestar = event.sest_class;
    const description = Array.isArray(event.description) ? event.description : [];

    const studentData =
      description
        .map((number) => {
          const status =
            parseInt(number.fcount, 10) === 1 && parseInt(number.attendance_status, 10) === 6
              ? 'Forfeit'
              : parseInt(number.wcount, 10) === 2 && parseInt(number.attendance_status, 10) === 6
                ? 'Writeoff'
                : parseInt(number.cancel_status, 10) === 2 &&
                    parseInt(number.attendance_status, 10) === 6
                  ? 'Cancelled'
                  : parseInt(number.is_paused, 10) === 1
                    ? 'Paused'
                    : parseInt(number.cancel_status, 10) === 0 &&
                        parseInt(number.cancel_defer_sources, 10) === 1
                      ? 'Deferment Requested'
                      : parseInt(number.cancel_status, 10) === 1 &&
                          parseInt(number.cancel_defer_sources, 10) === 1
                        ? 'Slot Assign'
                        : parseInt(number.cancel_status, 10) === 1
                          ? 'Deferment Requested'
                          : parseInt(number.cancel_status, 10) === 3 &&
                              parseInt(number.cancel_defer_sources, 10) === 2
                            ? 'One Off Requested'
                            : parseInt(number.attendance_status, 10) === 0
                              ? 'Pending'
                              : parseInt(number.attendance_status, 10) === 1
                                ? 'Ongoing'
                                : parseInt(number.attendance_status, 10) === 2
                                  ? 'Attended'
                                  : parseInt(number.attendance_status, 10) === 3
                                    ? 'Missed'
                                    : parseInt(number.attendance_status, 10) === 4
                                      ? 'Offline'
                                      : parseInt(number.attendance_status, 10) === 5
                                        ? 'Deferment Requested'
                                        : parseInt(number.attendance_status, 10) === 6
                                          ? 'Cancelled'
                                          : 'Pending';

          const labelFlexi =
            number.is_flexi && parseInt(number.is_flexi, 10) === 1 ? ' (Flexi)' : '';

          const abbreviation = number.st_year_level
            ? number.st_year_level
                .split(/(\d+)/)
                .filter(Boolean)
                .map((element, index) => (index === 0 ? element.charAt(0) : element))
                .join('')
            : '';

          const abborwords = abbreviation || number.st_year_level || '';
          const star =
            cls_typestar == 3 || (number.reassigned && number.reassigned == 1) ? '*' : '';

          return `${star}${capitalize(number.st_first_name)} ${number.st_surname} (${abborwords}${
            number.sub_abbreviation || ''
          })${labelFlexi} ${'-'} ${status}`;
        })
        .join('\n\n') || '-';

    tableRows.push([
      event.ses_date ? Moment(event.ses_date).format('DD-MM-YYYY') : '',
      timeRange,
      ses_name,
      tutor_name,
      studentData,
    ]);
  });

  doc.autoTable({
    head: [tableColumn],
    body: tableRows.length > 0 ? tableRows : [['No data available']],
    startY: 30,
    styles: {
      fontSize: 12,
      halign: 'start',
    },
    headStyles: {
      halign: 'start',
      fillColor: [100, 100, 100],
      textColor: 255,
      fontSize: 15,
    },
    columnStyles: {
      0: { halign: 'start' },
      1: { halign: 'start' },
      2: { halign: 'start' },
      3: { halign: 'start' },
      4: { halign: 'start' },
    },
  });

  const formattedName = center_name.replace(/['"]/g, '').replace(/\s+/g, '_').toLowerCase();
  const now = new Date();
  const timestamp =
    now.getFullYear() +
    ('0' + (now.getMonth() + 1)).slice(-2) +
    ('0' + now.getDate()).slice(-2) +
    '_' +
    ('0' + now.getHours()).slice(-2) +
    ('0' + now.getMinutes()).slice(-2) +
    ('0' + now.getSeconds()).slice(-2);

  doc.save(`${formattedName}_${timestamp}.pdf`);
}
